import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { type DebriefStage } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import { useDebriefFactsState } from '../../shared/context/DebriefFactsStateContext';
import { useDescribeState } from '../../describe-stage/context/DescribeStateContext';
import { useDeepDiveState } from '../../deep-dive-stage/context/DeepDiveStateContext';
import { STAGE_ORDER } from '../../gather-facts-stage/utils/stageMeta';
import { StageSelectRow } from './StageSelectRow';

const MAX_PICKS = 3;

interface MatchPointsPageProps {
  sessionId: string;
}

export function MatchPointsPage({ sessionId }: MatchPointsPageProps) {
  const { t } = useTranslation('debriefV2');
  const navigate = useNavigate();
  const { state } = useDescribeState();
  const { facts, factsByStage, isLoading } = useDebriefFactsState();
  const { state: deepDiveState, dispatch: deepDiveDispatch } =
    useDeepDiveState();
  const [picked, setPicked] = useState<Set<DebriefStage>>(
    () => new Set(deepDiveState.matchPoints.map((session) => session.stage))
  );

  useEffect(() => {
    if (!state.isComplete) {
      navigate({
        to: '/interactions/debrief-v2/$id/describe',
        params: { id: sessionId },
        replace: true,
      });
      return;
    }
    if (!isLoading && facts.length === 0) {
      navigate({
        to: '/interactions/debrief-v2/$id/gather',
        params: { id: sessionId },
        replace: true,
      });
    }
  }, [state.isComplete, isLoading, facts.length, navigate, sessionId]);

  const togglePick = (stage: DebriefStage) => {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(stage)) {
        next.delete(stage);
      } else {
        next.add(stage);
      }
      return next;
    });
  };

  const handleContinue = () => {
    const orderedPicks = STAGE_ORDER.filter((stage) => picked.has(stage));
    if (orderedPicks.length === 0) return;
    deepDiveDispatch({ type: 'COMMIT_MATCH_POINTS', payload: orderedPicks });
    navigate({
      to: '/interactions/debrief-v2/$id/deep-dive',
      params: { id: sessionId },
    });
  };

  const pickedCount = picked.size;
  const canContinue = pickedCount > 0;
  const capReached = pickedCount >= MAX_PICKS;

  return (
    <main className="flex-1 w-full max-w-[1040px] mx-auto pt-8 pb-24 px-6">
      <header className="mb-8 text-start">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">
          {t('matchPoints.pageTitle')}
        </h2>
        <p className="text-slate-500 text-base">
          {t('matchPoints.pageSubtitle')}
        </p>
      </header>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col mb-12">
        <div className="divide-y divide-slate-100 flex-1">
          {STAGE_ORDER.map((stage) => {
            const stageFacts = factsByStage[stage] ?? [];
            const isPicked = picked.has(stage);
            const hasFacts = stageFacts.length > 0;
            const canPick = hasFacts && (isPicked || !capReached);
            return (
              <StageSelectRow
                key={stage}
                stage={stage}
                facts={stageFacts}
                isPicked={isPicked}
                canPick={canPick}
                onTogglePick={() => togglePick(stage)}
              />
            );
          })}
        </div>

        <div className="px-5 md:px-8 py-4 bg-slate-50/50 border-t border-slate-200 flex justify-between items-center rounded-b-2xl">
          <span
            className={cn(
              'text-sm',
              pickedCount === 0
                ? 'text-slate-500 font-normal'
                : 'text-slate-700 font-medium'
            )}
          >
            {pickedCount === 0
              ? t('matchPoints.selectionFooter.hint')
              : t('matchPoints.selectionFooter.counted')(pickedCount)}
          </span>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className={cn(
              'px-6 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors',
              canContinue
                ? 'bg-button-primary text-white hover:bg-button-primary-hover shadow-sm active:scale-[0.98] cursor-pointer'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            )}
          >
            {t('matchPoints.selectionFooter.continue')}
            <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
          </button>
        </div>
      </div>
    </main>
  );
}
